import React from "react";
import PageWrapper from "../components/PageWrapper";
import NarrowContainer from "../components/NarrowContainer";
import SemiNarrowContainer from "../components/SemiNarrowContainer";
import Button from '../components/Buttons';
import GuideCtaTriple from "../components/GuideCtaTriple";
import FinancialAdviceHubCards from "../components/FinancialAdviceHubCards";
import GuideAppCta from "../components/GuideAppCta";
import { InternalLink } from "../components/Links";
import Seo from "../components/Seo";
import financialAdviceImg1 from "../images/guides/retirement-planning-1.png";
import financialAdviceImg2 from "../images/guides/retirement-planning-2.png";
import financialAdviceImg3 from "../images/guides/retirement-planning-3.png";
import financialAdviceImg4 from "../images/guides/retirement-planning-4.png";
import financialAdviceImg6 from "../images/guides/retirement-planning-6.png";
import financialAdviceImg7 from "../images/guides/retirement-planning-7.png";
import financialAdviceImg8 from "../images/guides/retirement-planning-8.png";
import financialAdviceImg9 from "../images/guides/retirement-planning-9.png";
import financialAdviceImg10 from "../images/guides/retirement-planning-10.png";
import financialAdviceAppImg from "../images/guides/retirement-planning-app.webp";

const FinancialAdviceGuide = () => (
    <PageWrapper
        pageTitle="The Complete Guide to Financial Advice"
        pageDescription="Expert financial advice for expats, covering investment management, retirement planning, and wealth management."
    >
        <NarrowContainer>
            <h1 className="text-4xl lg:text-5xl mb-6">The Complete Guide to Financial Advice</h1>
            <p className="text-semibold text-holborn-mid-blue text-lg">Finding the right financial advice for expats is the key to securing a happy and prosperous financial future.</p>
            <p className="leading-relaxed">Holborn Assets are one of the leading financial advisory groups around the globe. We specialise in providing quality, independent financial advice and wealth management solutions to the expat market. We manage over $2billion AUM for 20,000+ clients around the world and offer award-winning financial advice services for expatriates and high-net-worth clients in the 18 countries we operate in.</p>
            <p className="leading-relaxed">Whatever stage of your financial journey you’re currently in, speak to us today and we can help.</p>
            <h3 className="mt-12 mb-6">The importance of expat financial advice</h3>
            <p className="leading-relaxed">There are cross-border rules and laws to contend with when living and working abroad. As a result, expats typically face complex financial situations.</p>
            <p className="leading-relaxed">The best way to navigate these challenges is with expert financial guidance. A financial adviser can support you in various ways, offering services such as:</p>
            <ul className="leading-relaxed list-disc list-inside ml-4 mb-4">
                <li><InternalLink to="/our-services/tax-planning/">Tax planning</InternalLink></li>
                <li><InternalLink to="/our-services/investments/">Investments</InternalLink></li>
                <li><InternalLink to="/our-services/investments/education-planning/">Education Planning</InternalLink></li>
                <li><InternalLink to="/our-services/financial-planning/retirement-planning/">Retirement Planning</InternalLink></li>
                <li><InternalLink to="/our-services/financial-planning/pensions/">Pensions</InternalLink></li>
            </ul>
            <p className="leading-relaxed">Want to learn more? For a detailed breakdown, read our article about <InternalLink to="/financial-advice-guide/expat-financial-advice/">expat financial advice</InternalLink>.</p>
        </NarrowContainer>
        <SemiNarrowContainer>
            <img
                src={financialAdviceImg1}
                alt="Financial Advice Planning"
                className="w-full h-auto"
            />
        </SemiNarrowContainer>
        <NarrowContainer>
            <h3 className="mb-6">Finding a financial adviser in Dubai and the UAE</h3>
            <p className="leading-relaxed">Expats account for nearly 89% of the UAE’s population. In particular, Dubai has long been a hotspot for professionals.</p>
            <p className="leading-relaxed">And with no income tax on salaries or investments, expats have greater opportunities to build wealth. Working with an independent financial adviser will help you make the most of your situation and help maximise your money’s potential.</p>
            <p className="leading-relaxed">Looking for a financial adviser in the UAE? Our detailed guide has all the information you need to find a <InternalLink to="/financial-advice-guide/financial-advisers-in-dubai-and-the-uae/">financial adviser in Dubai and the UAE</InternalLink>.</p>
            <div className="my-12 flex">
                <Button
                    variant="green"
                    to="/enquiry-form/"
                    state={{ service: "Financial Planning" }}
                >
                    Get started
                </Button>
            </div>
        </NarrowContainer>
        <SemiNarrowContainer>
            <div className="grid grid-cols-3 gap-6">
                <div>
                    <img
                        src={financialAdviceImg2}
                        alt="Financial Advice Visual 2"
                        className="w-full h-auto rounded-lg"
                    />
                </div>
                <div>
                    <img
                        src={financialAdviceImg3}
                        alt="Financial Advice Visual 3"
                        className="w-full h-auto rounded-lg"
                    />
                </div>
                <div>
                    <img
                        src={financialAdviceImg4}
                        alt="Financial Advice Visual 4"
                        className="w-full h-auto rounded-lg"
                    />
                </div>
            </div>
        </SemiNarrowContainer>
        <NarrowContainer>
            <h3 className="mb-6">What is the difference between financial advice and financial planning?</h3>
            <p className="leading-relaxed">While closely related, there is a difference between financial planning and financial advice.</p>
            <p className="leading-relaxed">Financial advice refers to the guidance and support you receive from a professional to help you make informed decisions.</p>
            <p className="leading-relaxed">The scope of financial planning is broader. It is the process of creating a financial plan to help you achieve your short-term and long-term goals. While different, financial advice is part of the broader financial planning process.</p>
            <p className="leading-relaxed">Want to know more? Read our article on <InternalLink to="/financial-advice-guide/financial-advice-or-financial-planning/">the difference between financial advice and financial planning</InternalLink>.</p>
        </NarrowContainer>
        <GuideCtaTriple
            ctaUrl="/enquiry-form/"
            ctaState={{ service: "Financial Planning" }}
        />
        <NarrowContainer>
            <h3 className="mb-6">What are the benefits of financial advice?</h3>
            <p className="leading-relaxed">With so many financial products on the market, knowing which to choose can be a challenge.</p>
            <p className="leading-relaxed">An experienced, qualified adviser can help you save time and grow wealth. One of the main benefits of bespoke financial advice is it allows you to make more informed decisions, making it easier to reach your financial goals.</p>
            <p className="leading-relaxed">Want to know how you can make better financial decisions? Read our article on <InternalLink to="/financial-advice-guide/the-benefits-of-financial-advice/">the benefits of financial advice</InternalLink>.</p>
            <h3 className="mt-12 mb-6">How much does financial advice cost?</h3>
            <p className="leading-relaxed">Professional independent advice is not free and comes at a cost. As a client, it’s essential that you understand how fee structures work. That way, there are no surprises or hidden charges along the way.</p>
            <p className="leading-relaxed">Advisers typically apply fees using two structures:</p>
            <ul className="leading-relaxed list-disc list-inside ml-4 mb-4">
                <li>Percentage-based on the assets under management (AUM)</li>
                <li>Fixed fees</li>
                <li>In some cases, advisers or financial planners may use a combination of these fee structures.</li>
            </ul>
            <p className="leading-relaxed">Learn more about adviser fees – read our article on <InternalLink to="/financial-advice-guide/how-much-does-financial-advice-cost/">how much financial advice costs</InternalLink>.</p>
        </NarrowContainer>
        <SemiNarrowContainer>
            <div className="grid grid-cols-3 gap-6">
                <div>
                    <img
                        src={financialAdviceImg6}
                        alt="Financial Advice Visual 6"
                        className="w-full h-auto rounded-lg"
                    />
                </div>
                <div>
                    <img
                        src={financialAdviceImg7}
                        alt="Financial Advice Visual 7"
                        className="w-full h-auto rounded-lg"
                    />
                </div>
                <div>
                    <img
                        src={financialAdviceImg8}
                        alt="Financial Advice Visual 8"
                        className="w-full h-auto rounded-lg"
                    />
                </div>
            </div>
        </SemiNarrowContainer>
        <NarrowContainer>
            <h3 className="mb-6">How to choose a financial adviser</h3>
            <p className="leading-relaxed">Just about anyone can benefit from professional advice to better manage their money.</p>
            <p className="leading-relaxed">However, we all have different requirements and financial goals. For this reason, you need to know how to find the right financial adviser for your needs.</p>
            <p className="leading-relaxed">Looking to choose the right financial adviser for you? Read our guide on <InternalLink to="/financial-advice-guide/how-to-choose-a-financial-adviser/">how to choose a financial adviser</InternalLink>.</p>
            <h3 className="mt-12 mb-6">Financial advice for pensions</h3>
            <p className="leading-relaxed">Independent financial advice can help you maximise your pension and ensure your retirement planning is on track.</p>
            <p className="leading-relaxed">But there are also legal requirements to be aware of if you have a direct benefit (DB) pension. DB pensions with a transfer value of more than £30,000 require you to take financial advice by law.</p>
            <p className="leading-relaxed">Looking for some advice about your pension? Read our guide on <InternalLink to="/financial-advice-guide/financial-advice-for-pensions/">financial advice for pensions</InternalLink>.</p>
            <div className="my-12 flex">
                <Button
                    variant="green"
                    to="/enquiry-form/"
                    state={{ service: "Financial Planning" }}
                >
                    Book a free consultation
                </Button>
            </div>
        </NarrowContainer>
        <SemiNarrowContainer>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="col-span-1 h-96">
                    <img
                        src={financialAdviceImg9}
                        alt="Retirement Planning Visual 9"
                        className="w-full h-full rounded-xl shadow-lg object-cover"
                    />
                </div>
                <div className="col-span-1 md:col-span-2 h-96">
                    <img
                        src={financialAdviceImg10}
                        alt="Retirement Planning Visual 10"
                        className="w-full h-full rounded-xl shadow-lg object-cover"
                    />
                </div>
            </div>
        </SemiNarrowContainer>
        <NarrowContainer>
            <h3 className="mb-6">Financial advice tailored to you</h3>
            <p className="leading-relaxed">You can benefit from quality financial advice no matter what stage of your life you’re currently in or the job that you have. For example, <InternalLink to="/financial-advice-guide/financial-advice-for-retirement-planning/">financial advice for retirement planning</InternalLink> is essential for those looking to make the most of their golden years when their working life comes to an end. You might need <InternalLink to="/financial-advice-guide/financial-advice-for-investments/">financial advice for investments</InternalLink> to strategically grow your wealth ready for the next chapter of your life.</p>
            <p className="leading-relaxed">There can also be a large discrepancy in advice when it comes to gender – the gender pay gap is real and we believe not enough <InternalLink to="/financial-advice-guide/financial-advice-for-women/">financial advice for women</InternalLink> is accessible or provided by financial advisers who are women. Holborn Assets has a long history of working with some of the top female financial advisers in the industry, with hundreds of female clients benefitting from their advice.</p>
            <p className="leading-relaxed">You may also find that different advice is required for the intricacies or quirks of your chosen profession – <InternalLink to="/financial-advice-guide/financial-advice-for-medical-professionals/">financial advice for medical professionals</InternalLink> will require different planning and guidance compared to <InternalLink to="/financial-advice-guide/financial-advice-for-airline-pilots/">financial advice for airline pilots and airline employees</InternalLink>.</p>

            <h3 className="mt-12 mb-6">Are you a HENRY?</h3>
            <p className="leading-relaxed">A HENRY (High Earner, Not Rich Yet) is someone with an excellent income but hasn’t yet taken the necessary steps to build that disposable income into a wealth management strategy. These people benefit from bespoke <InternalLink to="/financial-advice-guide/financial-advice-for-high-earners/">financial advice for high earners</InternalLink> to maximise their wealth. Entrepreneurs or business owners can also earn a lot, which in turn means qualified <InternalLink to="/financial-advice-guide/financial-advice-for-business-owners/">financial advice for business owners</InternalLink> is required.</p>
            
            <h3 className="mt-12 mb-6">Ready for expert financial advice?</h3>
            <p className="leading-relaxed">While closely related, there is a difference between financial planning and financial advice.</p>
            <p className="leading-relaxed">Financial advice refers to the guidance and support you receive from a professional to help you make informed decisions.</p>
            <p className="leading-relaxed">The scope of financial planning is broader. It is the process of creating a financial plan to help you achieve your short-term and long-term goals. While different, financial advice is part of the broader financial planning process.</p>
            <p className="leading-relaxed">Want to know more? Read our article on <InternalLink to="/financial-advice-guide/financial-advice-or-financial-planning/">the difference between financial advice and financial planning</InternalLink>.</p>
            <div className="my-12 flex">
                <Button
                    variant="green"
                    to="/enquiry-form/"
                    state={{ service: "Financial Planning" }}
                >
                    Book a free consultation
                </Button>
            </div>
        </NarrowContainer>
        <FinancialAdviceHubCards />
        <GuideAppCta
            title="Manage your finances with the Holborn App"
            content="Track your investments, monitor your financial goals, and manage your financial documents securely, all in one place. Get real-time insights, easy access to your portfolio, and tools that keep your plan on track, wherever you are."
            guideImage={financialAdviceAppImg}
            imageAlt="Holborn App Interface showing investment tracking and financial features."
        />
    </PageWrapper>
);

export const Head = ({ location }) => {
    return (
        <Seo
            title="The Complete Guide to Financial Advice"
            description="Expert financial advice for expats, covering investment management, retirement planning, and wealth management."
            canonical={location.pathname}
        />
    );
};

export default FinancialAdviceGuide;